<template>
  <div class="login-container p-d-flex p-jc-center p-ai-center">
    <div class="login-card">
      <div class="logo-container">
        <img src="../assets/logo.png" alt="SimForce Logo" class="logo" />
        <h1>SimForce</h1>
      </div>

      <div v-if="error" class="error-message p-mb-3">
        <p-message severity="error" :closable="false">{{ error }}</p-message>
      </div>

      <div class="p-input-filled p-mb-3">
        <span class="p-float-label p-input-icon-right">
          <i class="pi pi-user"></i>
          <p-text id="username" v-model="username" type="text" class="p-inputtext-lg" style="width:100%" :class="{'p-invalid': submitted && !username}" />
          <label for="username">Username</label>
        </span>
        <small class="p-error" v-if="submitted && !username">Username is required</small>
      </div>

      <div class="p-input-filled p-mb-4">
        <span class="p-float-label p-input-icon-right">
          <i class="pi pi-lock"></i>
          <p-text id="password" v-model="password" type="password" class="p-inputtext-lg" style="width:100%" :class="{'p-invalid': submitted && !password}" />
          <label for="password">Password</label>
        </span>
        <small class="p-error" v-if="submitted && !password">Password is required</small>
      </div>

      <div class="p-d-flex p-jc-between p-mb-3">
        <div class="p-field-checkbox">
          <p-checkbox v-model="rememberMe" binary id="rememberMe" />
          <label for="rememberMe">Remember Me</label>
        </div>
        <div>
          <router-link to="/forgot-password" class="forgot-password">Forgot Password?</router-link>
        </div>
      </div>

      <p-button
        label="Login"
        icon="pi pi-sign-in"
        class="p-button-lg p-mb-3"
        style="width:100%"
        @click="handleLogin"
        :loading="loading"
      />

      <div class="p-mt-4 help-text">
        <p>Having trouble logging in? Contact your administrator.</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { clearAllAuthData } from '../components/auth-utils';

export default defineComponent({
  name: 'LoginView',
  setup() {
    const router = useRouter();
    const username = ref('');
    const password = ref('');
    const rememberMe = ref(false);
    const loading = ref(false);
    const submitted = ref(false);
    const error = ref('');

    // Only clear authentication data if:
    // 1. We're logging in for the first time, or
    // 2. We have a force=true parameter, or
    // 3. We have stale/invalid token data
    onMounted(async () => {
      const route = useRoute();
      const shouldClear =
        route.query.force === 'true' ||
        route.query.clean === 'true' ||
        (document.cookie.includes('API_TOKEN') && !localStorage.getItem('user'));

      if (shouldClear) {
        console.log('Login page mounted with force/clean flag or stale token, clearing auth data');

        try {
          // Add a small delay to ensure DOM is fully mounted
          await new Promise(resolve => setTimeout(resolve, 50));
          await clearAllAuthData(false);
          console.log('Login page: Authentication data cleared successfully');

          // Remove the force flag from URL to prevent endless clearing
          if (route.query.force === 'true' || route.query.clean === 'true') {
            router.replace({ query: { ...route.query, force: undefined, clean: undefined } });
          }
        } catch (e) {
          console.error('Login page: Error clearing authentication data:', e);
        }
      } else {
        console.log('Login page mounted normally, not clearing auth data');
      }
    });

    // Get route at setup level, not inside a function
    const route = useRoute();
    const store = useStore();

    const handleLogin = async () => {
      submitted.value = true;
      error.value = '';

      if (!username.value || !password.value) {
        return;
      }

      loading.value = true;
      try {
        await store.dispatch('login', {
          username: username.value,
          password: password.value,
          rememberMe: rememberMe.value
        });
        const redirectPath = route.query.redirect ? String(route.query.redirect) : '/';
        router.push(redirectPath);
      } catch (e: any) {
        error.value = (store.getters as any).error || e.message;
      } finally {
        loading.value = false;
      }
    };

    return {
      username,
      password,
      rememberMe,
      loading,
      submitted,
      error,
      handleLogin
    };
  }
});
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 2rem;
}

.login-card {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.logo-container {
  text-align: center;
  margin-bottom: 2rem;
}

.logo {
  width: 80px;
  height: auto;
  margin-bottom: 1rem;
}

h1 {
  color: #344767;
  font-weight: 600;
  margin: 0;
}

.error-message {
  margin-bottom: 1rem;
}

.forgot-password {
  color: #6366F1;
  text-decoration: none;
  font-size: 0.9rem;
}

.forgot-password:hover {
  text-decoration: underline;
}

.help-text {
  text-align: center;
  color: #64748B;
  font-size: 0.9rem;
}

@media (max-width: 480px) {
  .login-card {
    padding: 1.5rem;
  }
}
</style>